import {
  Box,
  Grid as ChakraGrid,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  Badge,
  CompactQuote,
  Divider,
  Grid,
  MotionBox,
} from '@time-line/components/common';
import { VerifiedTick } from '@time-line/components/icons';
import {
  ProductBenefitsContainer,
  RichTextResolver,
} from 'components/common/RichTextResolver';
import { AnimatePresence } from 'motion/react';
import { useState } from 'react';
import type {
  BeforeAfter as BeforeAfterType,
  BeforeAfterResult,
} from 'types/cms/blocks';

import { CLINICAL_RESULT_BADGE_TITLE } from './constants';
import { ConsumerResults } from './ConsumerResults';
import { Slider } from './Slider';
import { Thumbnail } from './Thumbnail';

export {
  CLINICAL_RESULT_BADGE_TITLE,
  SLIDER_CONTAINER_BREAKPOINT,
} from './constants';

type BeforeAfterProps = Omit<BeforeAfterType, '_type' | '_key'>;

function showProductLink(
  results?: BeforeAfterResult[],
  productFormatSlugs?: string[],
): boolean {
  const rows = results?.filter((r) => r.product?.slug) ?? [];
  if (!rows.length || !productFormatSlugs?.length) return true;
  return !rows.every((r) => productFormatSlugs.includes(r.product!.slug));
}

export function BeforeAfter({ results, productFormatSlugs }: BeforeAfterProps) {
  const [index, setIndex] = useState(0);
  const showProduct = showProductLink(results, productFormatSlugs);

  const hasMultipleResults = results && results.length > 1;
  const activeResult = results?.[index];
  const activeQuoteOrConsumerTrial = activeResult?.quoteOrConsumerTrial?.[0];

  return (
    <Grid className="section--before-after" my="2xl">
      <Grid.Inner gridRowGap={{ base: 'sm', md: 0 }}>
        <Box gridColumn={{ base: 'span 12', md: 'span 6' }}>
          <ChakraGrid
            gap={{ base: '5xs', md: '4xs' }}
            gridTemplateColumns={{
              base: '1fr',
              md: '1fr 80px',
            }}
            alignItems="start"
          >
            <Box display="grid">
              {results?.map((result, i) => (
                <MotionBox
                  key={i}
                  gridColumn="1"
                  gridRow="1"
                  initial={false}
                  animate={{
                    opacity: i === index ? 1 : 0,
                    zIndex: i === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  style={{
                    pointerEvents: i === index ? 'auto' : 'none',
                  }}
                >
                  <Slider {...result} showProduct={showProduct} />
                </MotionBox>
              ))}
            </Box>
            {hasMultipleResults && (
              <SimpleGrid
                gap={{ base: '5xs', md: '4xs' }}
                columns={{ base: Math.max(results.length, 3), md: 1 }}
                w={{ base: 'full', md: '80px' }}
              >
                {results.map((result, resultIndex) => (
                  <Thumbnail
                    key={resultIndex}
                    beforeImage={result.before.image}
                    afterImage={result.after.image}
                    isActive={resultIndex === index}
                    handleThumbnailClick={() => {
                      setIndex(resultIndex);
                    }}
                  />
                ))}
              </SimpleGrid>
            )}
          </ChakraGrid>
        </Box>
        <Stack
          gridColumn={{ base: 'span 12', md: '8 / span 5' }}
          gap="m"
          justifyContent="space-between"
        >
          <AnimatePresence mode="wait">
            {activeResult && (
              <MotionBox
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Stack gap="3xs">
                  {activeResult.isOverlineVisible && (
                    <Badge
                      w="fit-content"
                      leftIcon={<VerifiedTick boxSize="2xs" />}
                      leftIconWrapperProps={{
                        ml: '2px',
                        mr: '5xs',
                      }}
                      bg="none"
                      px="0"
                      colorScheme="lightgray"
                    >
                      {CLINICAL_RESULT_BADGE_TITLE}
                    </Badge>
                  )}
                  <Box position="relative">
                    <Stack gap="3xs">
                      <Text as="h2" size="h2" maxW="480px">
                        {activeResult.clinicalFactTitle}
                      </Text>
                      {activeResult.clinicalResultSubtitle && (
                        <>
                          <Divider w="full" />
                          <Box maxW="320px">
                            <RichTextResolver
                              blocks={activeResult.clinicalResultSubtitle}
                              container={ProductBenefitsContainer}
                            />
                          </Box>
                        </>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </MotionBox>
            )}
          </AnimatePresence>
          <AnimatePresence mode="wait">
            {activeQuoteOrConsumerTrial && (
              <MotionBox
                key={`before-after-quote-or-trial-${index}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {activeQuoteOrConsumerTrial._type ===
                'timeline_consumerTrial' ? (
                  <ConsumerResults
                    results={activeQuoteOrConsumerTrial.results}
                  />
                ) : (
                  <CompactQuote
                    responsiveBp="md"
                    {...activeQuoteOrConsumerTrial}
                  />
                )}
              </MotionBox>
            )}
          </AnimatePresence>
        </Stack>
      </Grid.Inner>
    </Grid>
  );
}
